/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpContext } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { ContainerSelectItemDto } from '../models/container-select-item-dto';
import { ContainerSortimentCreateRequest } from '../models/container-sortiment-create-request';
import { ContainerSortimentCreateResponse } from '../models/container-sortiment-create-response';
import { PageContainerSortimentPageResponse } from '../models/page-container-sortiment-page-response';

/**
 * Container sortiment resource endpoints.
 */
@Injectable({
  providedIn: 'root',
})
export class ContainerSortimentsService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /**
   * Path part for operation getContainerSortiment
   */
  static readonly GetContainerSortimentPath = '/sortiments/containers';

  /**
   * Gets containers sortiment.
   *
   * Finds and returns container sortiment of logged in merchant.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getContainerSortiment()` instead.
   *
   * This method doesn't expect any request body.
   */
  getContainerSortiment$Response(
    params?: {
      /**
       * Filter on brand
       */
      brand?: string;

      /**
       * Filter on name
       */
      name?: string;

      /**
       * Filter on amount
       */
      amount?: string;

      /**
       * Filter on unit
       */
      unit?: 'LITERS' | 'KILOGRAMS';

      /**
       * Filter on mainType of container
       */
      mainType?: 'STANDALONE' | 'COLLECTION';

      /**
       * Filter on subType of container
       */
      subType?: string;

      /**
       * Filter on ean
       */
      ean?: string;

      /**
       * Filter on fixSupplierName
       */
      fixSupplierName?: string;

      /**
       * Filter on expirationOfSupplierContract
       */
      expirationOfSupplierContract?: string;

      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<PageContainerSortimentPageResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ContainerSortimentsService.GetContainerSortimentPath,
      'get'
    );
    if (params) {
      rb.query('brand', params.brand, {});
      rb.query('name', params.name, {});
      rb.query('amount', params.amount, {});
      rb.query('unit', params.unit, {});
      rb.query('mainType', params.mainType, {});
      rb.query('subType', params.subType, {});
      rb.query('ean', params.ean, {});
      rb.query('fixSupplierName', params.fixSupplierName, {});
      rb.query(
        'expirationOfSupplierContract',
        params.expirationOfSupplierContract,
        {}
      );
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<PageContainerSortimentPageResponse>;
        })
      );
  }

  /**
   * Gets containers sortiment.
   *
   * Finds and returns container sortiment of logged in merchant.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getContainerSortiment$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getContainerSortiment(
    params?: {
      /**
       * Filter on brand
       */
      brand?: string;

      /**
       * Filter on name
       */
      name?: string;

      /**
       * Filter on amount
       */
      amount?: string;

      /**
       * Filter on unit
       */
      unit?: 'LITERS' | 'KILOGRAMS';

      /**
       * Filter on mainType of container
       */
      mainType?: 'STANDALONE' | 'COLLECTION';

      /**
       * Filter on subType of container
       */
      subType?: string;

      /**
       * Filter on ean
       */
      ean?: string;

      /**
       * Filter on fixSupplierName
       */
      fixSupplierName?: string;

      /**
       * Filter on expirationOfSupplierContract
       */
      expirationOfSupplierContract?: string;

      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<PageContainerSortimentPageResponse> {
    return this.getContainerSortiment$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<PageContainerSortimentPageResponse>) =>
          r.body as PageContainerSortimentPageResponse
      )
    );
  }

  /**
   * Path part for operation addContainerToSortiment
   */
  static readonly AddContainerToSortimentPath = '/sortiments/containers';

  /**
   * Creates a new instance of container sortiment.
   *
   * Adds a new container to the merchants container sortiment if it has not been added before.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `addContainerToSortiment()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  addContainerToSortiment$Response(
    params: {
      body: ContainerSortimentCreateRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<ContainerSortimentCreateResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ContainerSortimentsService.AddContainerToSortimentPath,
      'post'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<ContainerSortimentCreateResponse>;
        })
      );
  }

  /**
   * Creates a new instance of container sortiment.
   *
   * Adds a new container to the merchants container sortiment if it has not been added before.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `addContainerToSortiment$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  addContainerToSortiment(
    params: {
      body: ContainerSortimentCreateRequest;
    },
    context?: HttpContext
  ): Observable<ContainerSortimentCreateResponse> {
    return this.addContainerToSortiment$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<ContainerSortimentCreateResponse>) =>
          r.body as ContainerSortimentCreateResponse
      )
    );
  }

  /**
   * Path part for operation getContainerSelectItemsFromSortiment
   */
  static readonly GetContainerSelectItemsFromSortimentPath =
    '/sortiments/containers/select-items';

  /**
   * Gets container dropdown select items from users sortiment.
   *
   * Finds and returns container select items from sortiment of logged in merchant.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getContainerSelectItemsFromSortiment()` instead.
   *
   * This method doesn't expect any request body.
   */
  getContainerSelectItemsFromSortiment$Response(
    params: {
      subType: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<Array<ContainerSelectItemDto>>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ContainerSortimentsService.GetContainerSelectItemsFromSortimentPath,
      'get'
    );
    if (params) {
      rb.query('subType', params.subType, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<Array<ContainerSelectItemDto>>;
        })
      );
  }

  /**
   * Gets container dropdown select items from users sortiment.
   *
   * Finds and returns container select items from sortiment of logged in merchant.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getContainerSelectItemsFromSortiment$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getContainerSelectItemsFromSortiment(
    params: {
      subType: string;
    },
    context?: HttpContext
  ): Observable<Array<ContainerSelectItemDto>> {
    return this.getContainerSelectItemsFromSortiment$Response(
      params,
      context
    ).pipe(
      map(
        (r: StrictHttpResponse<Array<ContainerSelectItemDto>>) =>
          r.body as Array<ContainerSelectItemDto>
      )
    );
  }
}
